import type { AdminNotification } from '@shared/api/contracts/workspace';

export const NOTIFICATION_KINDS = [
  'withdrawal.requested',
  'withdrawal.approved',
  'withdrawal.rejected',
  'withdrawal.cancelled_by_merchant',
  'withdrawal.failed',
  'withdrawal.address_denied',
  'auto_withdrawal.changed',
  'auto_withdrawal.address_verified',
  'merchant.registered',
  'password_reset.requested',
  'password_reset.completed',
  'password.changed',
  '2fa.reset',
  '2fa.method_changed',
  'session.hijack_suspected',
  'login.locked',
  'withdrawals.locked',
  'withdrawal_address.changed',
  'api_access.changed',
  'project.created',
  'project.webhook_changed',
  'invoice.error',
  'refund.needs_attention',
  'aml.blocked',
  'aml.skipped_technical',
  'manual_review',
  'db.manual_change',
] as const;

const KNOWN = new Set<string>(NOTIFICATION_KINDS);

export const isKnownNotificationKind = (kind: string): boolean => KNOWN.has(kind);

const textOf = (value: unknown): string => (typeof value === 'string' || typeof value === 'number' ? String(value) : '');

export const notificationValues = (item: Pick<AdminNotification, 'params' | 'subject' | 'actor' | 'target'>): Record<string, string> => {
  const params = item.params ?? {};
  return {
    merchant: item.subject?.email || item.actor.email || '',
    amount: textOf(params.amount),
    asset: textOf(params.assetCode),
    network: textOf(params.network ?? params.networkCode),
    id: textOf(params.withdrawalId ?? item.target?.id),
    address: textOf(params.address ?? params.destinationAddress),
    target: textOf(params.targetType ?? item.target?.type),
    cause: textOf(params.cause),
    reason: textOf(params.reason),
    name: textOf(params.name),
  };
};

export const mergeNotifications = (
  current: AdminNotification[],
  incoming: AdminNotification[],
  truncated: boolean,
): { items: AdminNotification[]; inserted: number } => {
  if (truncated) return { items: incoming, inserted: incoming.length };
  const known = new Set(current.map((item) => item.id));
  const fresh = incoming.filter((item) => !known.has(item.id));
  const updates = new Map(incoming.map((item) => [item.id, item]));
  return {
    items: [...fresh, ...current.map((item) => updates.get(item.id) ?? item)],
    inserted: fresh.length,
  };
};

export const workspaceDocumentTitle = (pending: number, critical: number, base = 'Paygatex'): string => {
  const count = pending + critical;
  return count > 0 ? `(${count}) ${base}` : base;
};

export const STALE_WITHDRAWAL_MS = 15 * 60 * 1000;

export const withdrawalAgeMs = (createdAt: string, serverTime: string, now = Date.now()): number => {
  const skew = now - new Date(serverTime).getTime();
  return Math.max(0, now - skew - new Date(createdAt).getTime());
};
